import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { combineLatest, defer, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import Env from '../../env';
import MtbPlugin from './mtb.plugin';
import { DeviceKey } from '../ridango.models';
import { Temporal } from '@js-temporal/polyfill';
import { NetworkService } from './network.service';

@Injectable({
    providedIn: 'root'
})
export class MtbService {
    private static DEVICE_KEY_CACHE = 'BOB_KEY_CACHE';

    constructor(
        @Inject('ENV') private env: Env,
        private httpClient: HttpClient,
        private networkService: NetworkService
    ) {}

    generateAztec(mtb: string, key: DeviceKey): Observable<string> {
        return defer(async () => {
            return await MtbPlugin.generate({
                mtb: mtb,
                kid: key.kid,
                k: key.k,
                app: this.env.BOB?.APP_ID
            });
        }).pipe(map((r) => r.mtb));
    }

    getDeviceId(): Observable<string> {
        return defer(() => MtbPlugin.deviceId()).pipe(map((d) => d.deviceId));
    }

    getDeviceKey(): Observable<DeviceKey> {
        return combineLatest([this.getDeviceId(), this.networkService.getStatus()]).pipe(
            switchMap(([deviceId, online]) => {
                if (!online) {
                    return of(this.getCachedKey());
                }
                if (this.env.IS_NEW_AFC) {
                    return this.getDeviceKeyNewAfc(deviceId);
                } else {
                    return this.getDeviceKeyOldAfc(deviceId);
                }
            }),
            tap((key) => {
                localStorage.setItem(MtbService.DEVICE_KEY_CACHE, JSON.stringify(key));
            })
        );
    }

    private getCachedKey(): DeviceKey {
        const key: DeviceKey = JSON.parse(localStorage.getItem(MtbService.DEVICE_KEY_CACHE));
        if (!key) {
            throw new Error('NO_CACHED_DEVICE_KEY');
        }
        const expiration = Temporal.Instant.fromEpochSeconds(key.exp).subtract({ hours: 1 });
        if (Temporal.Instant.compare(Temporal.Now.instant(), expiration) >= 0) {
            throw new Error('CACHE_DEVICE_KEY_EXPIRED');
        }
        return key;
    }

    private getDeviceKeyNewAfc(deviceId: string) {
        const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/device/key/${deviceId}`;
        const headers = new HttpHeaders({
            Accept: 'application/json'
        });
        return this.httpClient.get<DeviceKey>(url, { headers }).pipe(
            map((response: DeviceKey) => {
                response.exp *= 1000;
                return response;
            })
        );
    }

    private getDeviceKeyOldAfc(deviceId: string) {
        const url = `${this.env.CORE_API_URL}/partner/v3/region/${this.env.REGION_ID}/bob/device/key`;
        const headers = new HttpHeaders({
            Accept: 'application/json'
        });
        return this.httpClient.post<DeviceKey>(url, { did: deviceId }, { headers });
    }
}
